import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  TextField,
  Button,
  Avatar,
  IconButton,
  Grid,
  Divider,
  Alert,
  InputAdornment,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PhotoCamera as PhotoCameraIcon } from '@mui/icons-material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AppMenu from '../../components/AppMenu';
import { useAuth } from '../../contexts/AuthContext';
import { userService } from '../../services/userService';
import ComingSoonTag from '../../components/ComingSoonTag';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  profileImage?: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AccountDetails: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    profileImage: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Load user profile data when component mounts
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const userData = await userService.getUserProfile();
        
        // Map API response to form data structure
        setFormData({
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          mobile: userData.phoneNumber || '',
          profileImage: userData.profilePictureUrl || '',
          address: {
            street: userData.address?.street || '',
            city: userData.address?.city || '',
            state: userData.address?.state || '',
            zipCode: userData.address?.zipCode || '',
            country: userData.address?.country || '',
          },
        });
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        // Could add error handling here (e.g., show error message to user)
      }
    };

    // Only fetch if user is authenticated
    if (isAuthenticated) {
      fetchUserProfile();
    }
  }, [isAuthenticated]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => {
        const parentObj = prev[parent as keyof FormData] as Record<string, string>;
        return {
          ...prev,
          [parent]: {
            ...parentObj,
            [child]: value,
          },
        };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateProfile = async () => {
    try {
      // Transform the form data to match the expected API format
      // Note: Email is not included as it cannot be updated
      const requestData = {
        email: formData.email, // Keep email for API compatibility, but it won't change
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.mobile,
        profilePictureUrl: formData.profileImage || ''
      };
      
      await userService.updateUserProfile(requestData);
      // Handle success - could add a success message here
      console.log('Profile updated successfully');
    } catch (error) {
      // Handle error - could add error handling here
      console.error('Failed to update profile:', error);
    }
  };

  const { changePassword } = useAuth();
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCurrentPasswordIcon, setShowCurrentPasswordIcon] = useState(false);
  const [showNewPasswordIcon, setShowNewPasswordIcon] = useState(false);
  const [showConfirmPasswordIcon, setShowConfirmPasswordIcon] = useState(false);

  const handleUpdatePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }
    
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordSuccess('Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      setPasswordError(error.response?.data?.message || 'Failed to update password. Please try again.');
    }
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleSignupClick = () => {
    navigate('/signup');
  };

  const handleClickShowCurrentPassword = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleCurrentPasswordFocus = () => {
    setShowCurrentPasswordIcon(true);
  };

  const handleCurrentPasswordBlur = () => {
    if (!showCurrentPassword) {
      setShowCurrentPasswordIcon(false);
    }
  };

  const handleNewPasswordFocus = () => {
    setShowNewPasswordIcon(true);
  };

  const handleNewPasswordBlur = () => {
    if (!showNewPassword) {
      setShowNewPasswordIcon(false);
    }
  };

  const handleConfirmPasswordFocus = () => {
    setShowConfirmPasswordIcon(true);
  };

  const handleConfirmPasswordBlur = () => {
    if (!showConfirmPassword) {
      setShowConfirmPasswordIcon(false);
    }
  };

  return (
    <Box>
      <AppMenu 
        currentPath="/account-details"
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
      />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ width: '100%', mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="account tabs"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Account Details" />
            <Tab label="Password & Security" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {/* Profile Header Section */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 2, md: 4 },
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                mb: 4
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {/* Profile Image Section */}
                <Box sx={{ mr: 4, position: 'relative' }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      border: '4px solid',
                      borderColor: 'primary.main',
                      overflow: 'hidden',
                      position: 'relative',
                      bgcolor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.6,
                      pointerEvents: 'none'
                    }}
                  >
                    {formData.profileImage ? (
                      <img
                        src={formData.profileImage}
                        alt="Profile"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          color: 'text.secondary'
                        }}
                      >
                        <PhotoCameraIcon sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="caption" textAlign="center">
                          Add Photo
                        </Typography>
                      </Box>
                    )}
                    
                                        {/* Coming Soon Tag */}
                    <ComingSoonTag
                      size="small"
                      position="absolute"
                      absolutePosition={{
                        top: '50%',
                        left: '50%'
                      }}
                      sx={{ zIndex: 0, transform: 'translate(-50%, -50%)' }}
                    />

                  </Box>
                </Box>
                
                {/* Profile Info */}
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="h4" 
                    color="primary"
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: '1.5rem', sm: '2.125rem' },
                      mb: 0
                    }}
                  >
                    Hi {formData.firstName}!
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Personal Information Section */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 2, md: 4 },
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                mb: 4
              }}
            >
              <Typography 
                variant="h5" 
                component="h2" 
                color="primary"
                fontWeight="bold"
                mb={3}
              >
                Personal Information
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    disabled
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.4)',
                        color: 'rgba(0, 0, 0, 0.4)'
                      },
                      '& .MuiInputLabel-root.Mui-disabled': {
                        color: 'rgba(0, 0, 0, 0.4)'
                      },
                      '& .MuiOutlinedInput-root.Mui-disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.12)',
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Address Information Section */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 2, md: 4 },
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                mb: 4,
                opacity: 0.6,
                pointerEvents: 'none',
                position: 'relative'
              }}
            >
              <ComingSoonTag 
                variant="box" 
                size="large"
                position="absolute"
                absolutePosition={{
                  top: '50%',
                  left: '50%'
                }}
                sx={{ 
                  zIndex: 1,
                  transform: 'translate(-50%, -50%)'
                }}
              />
              
              <Typography 
                variant="h5" 
                component="h2" 
                color="primary"
                fontWeight="bold"
                mb={3}
              >
                Address Information
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    name="address.street"
                    value={formData.address.street}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="address.city"
                    value={formData.address.city}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="State"
                    name="address.state"
                    value={formData.address.state}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    name="address.zipCode"
                    value={formData.address.zipCode}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    name="address.country"
                    value={formData.address.country}
                    disabled
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Update Button Section */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdateProfile}
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(95, 60, 111, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(95, 60, 111, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Update Profile
              </Button>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* Password & Security Section */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 2, md: 4 },
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                mb: 4
              }}
            >
              <Typography 
                variant="h5" 
                component="h2" 
                color="primary"
                fontWeight="bold"
                mb={3}
              >
                Password & Security
              </Typography>
              
              {passwordError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {passwordError}
                </Alert>
              )}
              {passwordSuccess && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {passwordSuccess}
                </Alert>
              )}
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    name="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: showCurrentPasswordIcon ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle current password visibility"
                            onClick={handleClickShowCurrentPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    onFocus={handleCurrentPasswordFocus}
                    onBlur={handleCurrentPasswordBlur}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="New Password"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: showNewPasswordIcon ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle new password visibility"
                            onClick={handleClickShowNewPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    onFocus={handleNewPasswordFocus}
                    onBlur={handleNewPasswordBlur}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: showConfirmPasswordIcon ? (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={handleClickShowConfirmPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    onFocus={handleConfirmPasswordFocus}
                    onBlur={handleConfirmPasswordBlur}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Update Password Button Section */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdatePassword}
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(95, 60, 111, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(95, 60, 111, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Update Password
              </Button>
            </Box>
          </TabPanel>
        </Box>
      </Container>
    </Box>
  );
};

export default AccountDetails; 